import { Type } from 'lucide-react';
import type { Source } from '@/data/fixtures';
import { useSourceRawText } from '@/hooks/lesson/useSources';
import {
  ViewerLoading,
  ViewerError,
} from '@/components/features/learning/sources/viewers/shell/ViewerStates';
import { ViewerShell } from '@/components/features/learning/sources/viewers/shell/ViewerShell';

interface TextViewerProps {
  source: Source;
  // Legacy callback retained for compatibility with the viewer-dispatch
  // shape; pasted-text sources don't have a file to download.
  onDownload?: (source: Source) => void;
}

/**
 * Renders pasted-text sources (the "Paste text" upload path). Content
 * lives directly on the Source row as `raw_text`, surfaced via
 * `GET /sources/{id}/text`. Whitespace is preserved and lines wrap so
 * the user sees their input as-is without scrollbar clipping.
 */
export function TextViewer({ source }: TextViewerProps) {
  const { data, isLoading, error } = useSourceRawText(source.id);

  if (isLoading) return <ViewerLoading />;
  if (error)
    return (
      <ViewerError
        message="Couldn't load text"
        detail={(error as Error).message}
      />
    );

  const text = data?.text || '';
  if (!text)
    return (
      <ViewerError
        message='Empty source'
        detail='This pasted-text source has no content.'
      />
    );

  return (
    <ViewerShell icon={Type} label='Text' tone='reference'>
      <div className='h-full w-full overflow-auto p-6'>
        <div className='mx-auto max-w-3xl whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground'>
          {text}
        </div>
      </div>
    </ViewerShell>
  );
}
